import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import MobileLayout from '../MobileLayout';
import { WaveListItem, WaveResponse } from '../../../services/fulfillment/types';
import { WAVE_TYPE_LABELS } from '../../../services/fulfillment/constants';
import { claimWave, listWaves } from '../../../services/fulfillment/waveApi';

export const routeForWaveType = (waveId: number, waveType: string): string =>
  waveType === 'paa_only'
    ? `/mobile/pick/paa-wave/${waveId}`
    : waveType === 'mixed'
    ? `/mobile/pick/mixed-wave/${waveId}`
    : waveType === 'wheels_frames_only'
    ? `/mobile/pick/wheels-frames-wave/${waveId}`
    : `/mobile/pick/bike-wave/${waveId}`;

const PickHome: React.FC = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [availableWaves, setAvailableWaves] = useState<WaveListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [takeOver, setTakeOver] = useState<{
    waveId: number;
    waveType: string;
    owner: string;
  } | null>(null);

  useEffect(() => {
    listWaves('in_progress')
      .then(waves => setAvailableWaves(waves))
      .catch(() => setError('Failed to load available waves.'))
      .finally(() => setLoading(false));
  }, []);

  const enterWave = (wave: WaveResponse, waveType: string) => {
    history.push(routeForWaveType(wave.waveId, waveType), { wave });
  };

  const handleStartPicking = async (waveId: number, waveType: string, force = false) => {
    setActionLoading(true);
    setError(null);
    try {
      const result = await claimWave(waveId, force);
      if ('error' in result) {
        if (result.error === 'owned_by_other') {
          setTakeOver({ waveId, waveType, owner: result.assignedToName ?? 'another picker' });
        } else {
          setError('Failed to start this wave — please try again.');
        }
        return;
      }
      setTakeOver(null);
      enterWave(result, waveType);
    } catch {
      setError('Failed to load wave.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <MobileLayout>
      <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
        Pick
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : availableWaves.length === 0 ? (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No active waves
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ask a manager to create a wave from the Fulfillment Orders screen.
            </Typography>
            <Button variant="outlined" fullWidth onClick={() => history.push('/mobile')}>
              Return Home
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {availableWaves.map(wave => (
            <Card key={wave.waveId}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                  <Typography variant="h6" gutterBottom>
                    {WAVE_TYPE_LABELS[wave.type]} Wave #{wave.waveId}
                  </Typography>
                  {wave.assignedToName && (
                    <Chip size="small" color="warning" label={`Picking: ${wave.assignedToName}`} />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {wave.completedOrders} of {wave.totalOrders} orders completed
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleStartPicking(wave.waveId, wave.type)}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <CircularProgress size={20} />
                  ) : wave.assignedToName ? (
                    'Take Over & Pick'
                  ) : (
                    'Start Picking'
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={!!takeOver} onClose={() => setTakeOver(null)}>
        <DialogTitle>Take over this wave?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This wave is currently being picked by <strong>{takeOver?.owner}</strong>. Taking it
            over will remove them from the wave. Continue only if they&apos;ve stopped picking it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTakeOver(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={actionLoading}
            onClick={() => takeOver && handleStartPicking(takeOver.waveId, takeOver.waveType, true)}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Take Over'}
          </Button>
        </DialogActions>
      </Dialog>
    </MobileLayout>
  );
};

export default PickHome;
